import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectTowerType, setTowerType } from "../redux/slices/towerSlice";
import { STATENAMES } from "../constants/states";
import { useInteractiveTooltips } from "../hooks/useInteractiveTooltips";

const DynamicForm = ({
  componentId,
  questionId,
  initialValue,
  onAnswerChange,
  formAnswers,
  className,
  unit,
}) => {
  const [question, setQuestion] = useState(null);
  const [answers, setAnswers] = useState({});
  const currentTowerType = useSelector(selectTowerType);
  const dispatch = useDispatch();
  const globalTowerType = useSelector(selectTowerType);
  const [opportunityIds, setOpportunityIds] = useState([]);

  useInteractiveTooltips(
    {
      placement: "top",
      html: true,
    },
    [question, answers]
  );

  useEffect(() => {
    if (initialValue) {
      // For states, initialValue should already be the 2-letter code
      setAnswers((prev) => ({
        ...prev,
        [questionId]: initialValue,
      }));

      if (questionId === "type") {
        //tower type
        dispatch(setTowerType(initialValue));
      }
    }
  }, [initialValue, questionId]);

  // Add effect to set default for louver_exists_xf when tower type is crossflow
  useEffect(() => {
    if (
      currentTowerType === "crossflow" &&
      componentId === "louvers" &&
      (questionId === "louver_exists_xf" ||
        (Array.isArray(questionId) &&
          questionId.some((q) => q.id === "louver_exists_xf")))
    ) {
      setAnswers((prev) => ({
        ...prev,
        louver_exists_xf: "yes",
      }));
      onAnswerChange({
        ...answers,
        louver_exists_xf: "yes",
      });
    }
  }, [currentTowerType, componentId, questionId]);

  useEffect(() => {
    if (questionId === "dealId") {
      const fetchOpportunityIds = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/opportunity/all-opportunities`
          );
          const data = await response.json();
          const ids = data.data.map((opp) => ({
            value: opp.data.OpportunityID,
            label: `${opp.data.Account_Name.name} - ${opp.data.Deal_Name} (${opp.data.OpportunityID})`,
          }));
          setOpportunityIds(ids);
        } catch (error) {
          console.log("Error fetching opportunity IDs:", error);
        }
      };

      fetchOpportunityIds();
    }
  }, []);

  useEffect(() => {
    const loadComponentData = async () => {
      try {
        const componentData = (await import(`../data/${componentId}.json`))
          .default;
        let targetQuestion;

        if (Array.isArray(questionId)) {
          const relevantQuestion = questionId.find(
            (q) => q.towerType === globalTowerType
          );
          if (relevantQuestion) {
            // Handle mechanical component's nested structure
            if (componentId === "mechanical") {
              for (const subComponent of componentData.subComponents) {
                const found = subComponent.questions.find(
                  (q) => q.id === relevantQuestion.id
                );
                if (found) {
                  targetQuestion = found;
                  break;
                }
              }
            } else {
              targetQuestion = componentData.questions.find(
                (q) => q.id === relevantQuestion.id
              );
            }
          }
        } else {
          // Handle mechanical component's nested structure
          if (componentId === "mechanical") {
            for (const subComponent of componentData.subComponents) {
              const found = subComponent.questions.find(
                (q) => q.id === questionId
              );
              if (found) {
                targetQuestion = found;
                break;
              }
            }
          } else {
            targetQuestion = componentData.questions.find(
              (q) => q.id === questionId
            );
          }
        }
        setQuestion(targetQuestion);
      } catch (error) {
        console.error("Error loading component data:", error);
      }
    };

    loadComponentData();
  }, [componentId, questionId, globalTowerType]);

  useEffect(() => {
    if (className) {
      setTimeout(() => {
        const element = document.querySelector(".form-field.invalid-field");
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          element.focus();
        }
      }, 100);
    }
  }, [className]);

  const handleChange = (value, questionId) => {
    const originalqid = questionId;
    if (Array.isArray(questionId)) {
      const relevantQuestion = questionId.find(
        (q) => q.towerType === currentTowerType
      );
      if (relevantQuestion) {
        questionId = relevantQuestion.id;
      }
    }

    console.log("handleChange:", questionId, originalqid, value);

    // Handle tower type selection
    if (componentId === "towerType" && questionId === "tower_type") {
      const newAnswers = {
        ...answers,
        [questionId]: value,
      };

      // Only reset subtype if tower type actually changes
      if (value !== answers[questionId]) {
        // Set the appropriate subtype key based on tower type
        const subtypeKey =
          value === "crossflow" ? "tower_subtype_xf" : "tower_subtype_cf";
        newAnswers[subtypeKey] = ""; // Reset only the subtype for the new tower type

        // Reset back_to_back subquestions if switching to crossflow
        if (value === "crossflow" && newAnswers["cf_tower_backtoback_type"]) {
          delete newAnswers["cf_tower_backtoback_type"];
        }

        // Keep the previous tower type until we successfully set the new one
        const prevTowerType = answers[questionId];

        // Update Redux state
        dispatch(setTowerType(value));

        // Update local state
        setAnswers(newAnswers);

        // Pass both the tower type and current answers, maintaining the previous type until transition
        onAnswerChange({
          ...newAnswers,
          tower_type: value,
          currentTowerType: value,
          previousTowerType: prevTowerType,
        });
      } else {
        // If the tower type hasn't changed, just maintain the current state
        setAnswers(newAnswers);
        onAnswerChange({
          ...newAnswers,
          tower_type: value,
          currentTowerType: value,
        });
      }
      return;
    }

    // Handle subtype selection separately
    if (
      questionId === "tower_subtype_xf" ||
      questionId === "tower_subtype_cf"
    ) {
      const newAnswers = {
        ...answers,
        [questionId]: value,
      };
      setAnswers(newAnswers);
      onAnswerChange({
        ...newAnswers,
        [questionId]: value,
        currentSubtype: value,
      });
      return;
    }

    if (questionId.includes("_cf") || questionId.includes("_xf")) {
      const baseQuestionId = questionId.replace(/_[cx]f$/, ""); // Removes _cf or _xf suffix
      console.log("baseQuestionId:", baseQuestionId);
      const newAnswers = {
        ...answers,
        [originalqid]: value,
        [baseQuestionId]: value, // Store under the base question ID
      };
      setAnswers(newAnswers);
      onAnswerChange(newAnswers);
      return;
    }

    //THIS MADE THE FORM RE-UPDATE CORRECTLY
    //BUT, the value did not save to the database
    if (questionId.includes("louver_blade_width")) {
      const newAnswers = {
        ...answers,
        [originalqid]: value, //original entry, but incorrect as object,object
      };
      setAnswers(newAnswers);
      onAnswerChange({
        ...answers,
        louverBladeWidth: value,
      });
      return;
    }

    // Handle all other changes
    const newAnswers = {
      ...answers,
      [questionId]: value,
    };
    setAnswers(newAnswers);
    onAnswerChange(newAnswers);
  };

  const findParentObject = (fobj, targetKey) => {
    if (!fobj || typeof fobj !== "object") return null;

    // Check if the key exists directly in the object
    if (targetKey in fobj) return fobj;

    // Check nested objects
    for (const [key, value] of Object.entries(fobj)) {
      if (value && typeof value === "object") {
        if (targetKey in value) {
          return value;
        }

        // Recursively search in nested objects
        const result = findParentObject(value, targetKey);
        if (result) return result;
      }
    }
    return null;
  };

  const renderSubQuestions = (option, parentValue) => {
    if (option && option.value === parentValue && option.subQuestions) {
      // Convert to array if it's an object
      const subQuestions = Array.isArray(option.subQuestions)
        ? option.subQuestions
        : [option.subQuestions];

      // Ensure parentObj is never null by providing empty object as fallback
      const parentObj = findParentObject(formAnswers, question?.id) || {};

      return subQuestions.map((subQ) => {
        if (!subQ || subQ.exclude_on_createtower) return null;
        return (
          <div key={subQ.id} className="ms-4 mt-2">
            <label>{subQ.text}</label>
            {renderField(
              subQ,
              parentObj[subQ.id],
              (value) => {
                let newAnswers;
                if (subQ.id.includes("_cf") || subQ.id.includes("_xf")) {
                  const baseQuestionId = subQ.id.replace(/_[cx]f/, ""); // Removes _cf or _xf suffix
                  newAnswers = {
                    ...answers,
                    [subQ.id]: value,
                    [baseQuestionId]: value, // Store under the base question ID
                  };
                } else newAnswers = { ...answers, [subQ.id]: value };
                setAnswers(newAnswers);
                onAnswerChange(newAnswers);
              },
              className,
              subQ.unit || ""
            )}
          </div>
        );
      });
    }
    return null;
  };
  const renderField = (
    question,
    value,
    onChange,
    className = "",
    unit = ""
  ) => {
    if (!question) return null;

    if (!value && question.id && question.id !== "tower_notes") {
      className += " invalid-field";
    }

    // Get the unit to display - from question or passed prop
    const displayUnit = question.unit || unit;

    if (question.useStateConstants) {
      return (
        <select
          className={`form-select ${className || ""}`}
          value={value || ""} // value is the 2-letter code from DB
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select State</option>
          {Object.entries(STATENAMES).map(([stateName, stateCode]) => (
            <option
              key={stateCode}
              value={stateCode}
              // selected={stateCode === value} // Match against 2-letter code
            >
              {stateName}
            </option>
          ))}
        </select>
      );
    }

    // console.log("Rendering renderField for:", question, value, answers);
    switch (question.type) {
      case "dropdown":
        let options = Array.isArray(question.options)
          ? question.options
          : question.options?.[currentTowerType] || [];

        if (question.id === "dealId") {
          options = opportunityIds;
        }

        // Default to yes for louver_exists_xf when tower type is crossflow
        let defaultValue = "";
        if (
          currentTowerType === "crossflow" &&
          question.id &&
          question.id === "louver_exists_xf"
        ) {
          defaultValue = "yes";
        }

        return (
          <>
            <select
              className={`form-select ${className || ""}`}
              value={value || defaultValue}
              onChange={(e) => onChange(e.target.value)}
              key={`${componentId}_${question.id || "unknown"}`}
            >
              <option value="">Select Option</option>
              {options &&
                Array.isArray(options) &&
                options.map((opt, index) => {
                  if (!opt) return null;
                  const uniqueKey = `option_${componentId}_${String(
                    opt.value || ""
                  )}_${index}`;
                  return (
                    <option key={uniqueKey} value={opt.value || ""}>
                      {opt.label || ""}
                    </option>
                  );
                })}
            </select>
            {/* Only render subQuestions if options exist and value matches */}
            {options &&
              Array.isArray(options) &&
              options.map((opt) =>
                renderSubQuestions(opt, value || defaultValue)
              )}
          </>
        );
      case "number":
        return (
          <div className="input-group">
            <input
              type="number"
              min="0"
              step="0.01"
              className={`form-control ${className || ""}`}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder={question.placeholder || ""}
            />
            {displayUnit && (
              <span className="input-group-text">{displayUnit}</span>
            )}
          </div>
        );
      case "textarea":
        return (
          <div className="input-group">
            <textarea
              className={`form-control ${className || ""}`}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder={question.placeholder || ""}
            />
            {displayUnit && (
              <span className="input-group-text">{displayUnit}</span>
            )}
          </div>
        );
      case "text":
        return (
          <div className="input-group">
            <input
              type="text"
              className={`form-control ${className || ""}`}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder={question.placeholder || ""}
            />
            {displayUnit && (
              <span className="input-group-text">{displayUnit}</span>
            )}
          </div>
        );
      default:
        return (
          <div className="input-group">
            <input
              type="text"
              className={`form-control ${className || ""}`}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder={question.placeholder || ""}
            />
            {displayUnit && (
              <span className="input-group-text">{displayUnit}</span>
            )}
          </div>
        );
    }
  };

  return (
    <div>
      {question && (
        <div className="mb-3">
          <label className="form-label">
            {question.text}
            {question.tooltip && (
              <i
                className="bi bi-info-circle ms-2"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-html="true"
                title={question.tooltip}
                style={{ cursor: "pointer" }}
              />
            )}
          </label>
          {renderField(
            question,
            answers[questionId],
            (value) => handleChange(value, questionId),
            className,
            question.unit || unit
          )}
          {question.subQuestions &&
            renderSubQuestions(question.subQuestions, answers[questionId])}
        </div>
      )}
    </div>
  );
};

export default DynamicForm;
